// Link manager for CRD editor
let linkPopover = null;
let allCrds = [];
let recentCrds = [];
let githubConfig = null;

export async function initLinkManager() {
  // Initialize as empty array first
  allCrds = [];
  console.log('🔗 initLinkManager starting...');
  
  // Fetch GitHub config
  try {
    const res = await fetch('/api/config');
    githubConfig = await res.json();
    console.log('⚙️ GitHub config:', githubConfig);
  } catch (error) {
    console.error('Failed to load GitHub config:', error);
  }
  
  // Fetch all CRDs for search
  try {
    console.log('📡 Fetching /api/all-crds...');
    const res = await fetch('/api/all-crds');
    const data = await res.json();
    console.log('📦 Response:', data);
    allCrds = data.crds || []; // Use empty array if undefined
    console.log('✅ Loaded CRDs:', allCrds);
  } catch (error) {
    console.error('Failed to load CRDs for linking:', error);
    allCrds = []; // Ensure it's an array even on error
  }
  
  // Load recent CRDs from localStorage
  const stored = localStorage.getItem('recentCrds');
  if (stored) {
    recentCrds = JSON.parse(stored);
  }
}

export function trackRecentCrd(crdPath) {
  // Add to recent, remove duplicates, keep last 10
  recentCrds = [crdPath, ...recentCrds.filter(p => p !== crdPath)].slice(0, 10);
  localStorage.setItem('recentCrds', JSON.stringify(recentCrds));
}

function createLinkPopover() {
  const popover = document.createElement('div');
  popover.className = 'link-popover';
  popover.innerHTML = `
    <div class="link-popover-content">
      <input type="text" class="link-input" placeholder="Paste link or search CRDs" />
      <div class="link-results"></div>
      <div class="link-actions">
        <button class="link-cancel">Cancel</button>
        <button class="link-apply">Apply</button>
      </div>
    </div>
  `;
  
  document.body.appendChild(popover);
  return popover;
}

function filterCrds(query) {
  if (!query) {
    // Show recents
    if (!allCrds || allCrds.length === 0) {
      return [];
    }
    return allCrds.filter(crd => recentCrds.includes(crd.path)).slice(0, 5);
  }
  
  // Check if it's a URL
  if (query.startsWith('http://') || query.startsWith('https://')) {
    return null; // External URL
  }
  
  // Search CRDs
  if (!allCrds || allCrds.length === 0) {
    return [];
  }
  const q = query.toLowerCase();
  return allCrds.filter(crd => 
    crd.name.toLowerCase().includes(q) ||
    crd.folder.toLowerCase().includes(q)
  ).slice(0, 10);
}

function renderCrdResults(crds, container) {
  console.log('🎨 renderCrdResults called with', crds?.length || 0, 'CRDs');
  if (!crds || crds.length === 0) {
    container.innerHTML = '';
    container.style.display = 'none';
    return;
  }
  
  container.style.display = 'block';
  container.innerHTML = `
    ${crds.length > 0 && !linkPopover.querySelector('.link-input').value ? '<div class="link-results-header">Recents</div>' : ''}
    ${crds.map(crd => `
      <div class="link-result-item" data-url="${crd.url}" data-path="${crd.path}">
        <div class="link-result-name">${crd.name}</div>
        <div class="link-result-folder">${crd.folder}</div>
      </div>
    `).join('')}
  `;
  
  // Add click handlers
  container.querySelectorAll('.link-result-item').forEach(item => {
    item.addEventListener('click', () => {
      const input = linkPopover.querySelector('.link-input');
      input.value = item.dataset.url;
      input.dataset.crdPath = item.dataset.path;
      container.style.display = 'none';
    });
  });
}

export function showLinkPopover(editor, existingHref = '') {
  if (!linkPopover) {
    linkPopover = createLinkPopover();
  }
  
  const input = linkPopover.querySelector('.link-input');
  const results = linkPopover.querySelector('.link-results');
  const cancelBtn = linkPopover.querySelector('.link-cancel');
  const applyBtn = linkPopover.querySelector('.link-apply');
  
  // Set existing link if editing
  input.value = existingHref;
  input.dataset.crdPath = '';
  
  // Show recents initially
  const recentCrdItems = filterCrds('');
  renderCrdResults(recentCrdItems, results);
  
  // Position popover near selection
  const { from } = editor.state.selection;
  const coords = editor.view.coordsAtPos(from);
  linkPopover.style.position = 'fixed';
  linkPopover.style.left = coords.left + 'px';
  linkPopover.style.top = (coords.bottom + 8) + 'px';
  linkPopover.style.display = 'block';
  
  // Focus input
  setTimeout(() => input.focus(), 0);
  
  // Handle input changes
  const handleInput = () => {
    console.log('🔎 Input changed:', input.value);
    const query = input.value;
    const filtered = filterCrds(query);
    console.log('📋 Filtered:', filtered?.length || 0, 'results');
    renderCrdResults(filtered, results);
  };
  
  input.removeEventListener('input', handleInput);
  input.addEventListener('input', handleInput);
  
  // Handle cancel
  const handleCancel = () => {
    linkPopover.style.display = 'none';
  };
  
  cancelBtn.removeEventListener('click', handleCancel);
  cancelBtn.addEventListener('click', handleCancel);
  
  // Handle apply
  const handleApply = () => {
    const url = input.value.trim();
    if (!url) {
      // Remove link
      editor.chain().focus().unsetLink().run();
    } else {
      // Set link
      editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
      
      // Track if it's a CRD
      if (input.dataset.crdPath) {
        trackRecentCrd(input.dataset.crdPath);
      }
    }
    
    linkPopover.style.display = 'none';
  };
  
  applyBtn.removeEventListener('click', handleApply);
  applyBtn.addEventListener('click', handleApply);
  
  // Handle Enter key
  const handleKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };
  
  input.removeEventListener('keydown', handleKeydown);
  input.addEventListener('keydown', handleKeydown);
}

export function hideLinkPopover() {
  if (linkPopover) {
    linkPopover.style.display = 'none';
  }
}

// Click outside to close
document.addEventListener('click', (e) => {
  if (linkPopover && !e.target.closest('.link-popover') && !e.target.closest('[data-cmd="link"]')) {
    hideLinkPopover();
  }
});